import { useCallback, useEffect, useState } from 'react';
import { NotesList } from './NotesList';
import { NotePostForm } from './NotePostForm';
import { listNotes, type PublicNote } from '../api/notes';

interface LandmarkNotesTabProps {
  landmarkId: string;
}

export function LandmarkNotesTab({ landmarkId }: LandmarkNotesTabProps) {
  const [notes, setNotes] = useState<PublicNote[]>([]);
  const [pending, setPending] = useState<PublicNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch notes whenever the selected landmark changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPending([]);
    listNotes(landmarkId)
      .then((rows) => {
        if (!cancelled) setNotes(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [landmarkId]);

  const handleSubmitStart = useCallback((optimistic: PublicNote): string => {
    setPending((prev) => [optimistic, ...prev]);
    return optimistic.id;
  }, []);

  const handleSubmitSuccess = useCallback((tempId: string, real: PublicNote) => {
    setPending((prev) => prev.filter((p) => p.id !== tempId));
    setNotes((prev) => [real, ...prev]);
  }, []);

  const handleSubmitError = useCallback((tempId: string) => {
    setPending((prev) => prev.filter((p) => p.id !== tempId));
  }, []);

  // Pending notes render first (newest), then confirmed notes.
  const merged = [...pending, ...notes];

  return (
    <div data-testid="landmark-notes-tab">
      <NotesList notes={merged} loading={loading} error={error} />
      <NotePostForm
        landmarkId={landmarkId}
        onSubmitStart={handleSubmitStart}
        onSubmitSuccess={handleSubmitSuccess}
        onSubmitError={handleSubmitError}
      />
    </div>
  );
}
